/******************************************************************
 * 파일명	: frontend/src/helpers/localizedField.js
 * 설명		: 응답의 언어별 필드(`name` → `nameEng`·`nameVie`·`nameChn`) 중 현재 언어 값을 고른다
 *          번역값이 없거나 공백뿐이면 원본(한국어) 필드를 돌려준다.
 *          snake_case 키(`comm_code_name` → `comm_code_name_eng`)는 `_eng`·`_vie`·`_chn`을 붙인다.
 *          근무 유형(WorkTypeDto)처럼 원본은 `abntdWorkTypNm`, 번역은 `nameEng`인 객체는
 *          options.localizedBase로 번역 필드의 기준 이름을 따로 넘긴다.
 *          표시 전용이다. 원본 `name`은 여러 화면에서 한글 리터럴과 비교하는 식별자로도 쓰이므로
 *          이 결과로 응답·store의 원본 필드를 덮어쓰지 않는다.
 ******************************************************************/

import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

/** 언어 코드(helpers/locales.jsx 기준) → 필드 접미사. 한국어는 원본 필드를 쓴다 */
export const LANG_FIELD_SUFFIX = { en: 'Eng', vi: 'Vie', ch: 'Chn' };

/** 근무 유형 객체(workType)는 원본 `abntdWorkTypNm`, 번역 `nameEng` 형태다 */
export const WORK_TYPE_NAME_OPTIONS = { localizedBase: 'name' };

/** 결재 목록 응답(/request/adminList 등)은 원본 `rank`·`department`, 번역 `rankNameEng`·`departmentNameEng` 형태다 */
export const RANK_NAME_OPTIONS = { localizedBase: 'rankName' };
export const DEPARTMENT_NAME_OPTIONS = { localizedBase: 'departmentName' };

const baseLanguage = (language) => String(language || 'ko').split('-')[0].toLowerCase();

const hasText = (value) => typeof value === 'string' && value.trim() !== '';

/** 현재 언어에서 읽을 필드명. 한국어·미지원 언어는 원본 필드명 */
export const getLocalizedFieldKey = (field, language, localizedBase = field) => {
	const suffix = LANG_FIELD_SUFFIX[baseLanguage(language)];
	if (!suffix) return field;
	return localizedBase.includes('_') ? `${localizedBase}_${suffix.toLowerCase()}` : `${localizedBase}${suffix}`;
};

/**
 * @param {object} obj       원본 필드와 언어별 필드를 가진 객체(없어도 됨)
 * @param {string} field     원본 필드명(기본 'name')
 * @param {string} language  현재 언어(i18n.language). 생략하면 i18next의 현재 언어
 * @param {object} options   { localizedBase } 번역 필드의 기준 이름이 원본과 다를 때
 */
export const getLocalizedField = (obj, field = 'name', language = i18n.language, options = {}) => {
	if (!obj) return undefined;
	const key = getLocalizedFieldKey(field, language, options.localizedBase || field);
	if (key !== field && hasText(obj[key])) return obj[key].trim();
	return obj[field];
};

export const getLocalizedName = (obj, language) => getLocalizedField(obj, 'name', language);

/**
 * API 번역값 → 번역 JSON(t) → 원본 순으로 고른다.
 * 기존에 `t(x.name)`으로 한글 원본을 번역 키로 쓰던 곳을 바꿀 때 쓴다.
 * API에 번역값이 있으면 JSON의 같은 키(예: "센터장")보다 우선한다.
 * @param {function} t  i18next의 t. 없으면 원본을 그대로 쓴다
 */
export const getLocalizedFieldOrTranslate = (obj, field = 'name', language = i18n.language, t, options = {}) => {
	if (!obj) return undefined;
	const key = getLocalizedFieldKey(field, language, options.localizedBase || field);
	if (key !== field && hasText(obj[key])) return obj[key].trim();
	const original = obj[field];
	return typeof t === 'function' && hasText(original) ? t(original) : original;
};

/**
 * `t(x.name)`을 그대로 바꿔 끼우는 용도. 이미 t를 쓰는 화면은 t가 바뀔 때 리렌더되므로 훅 없이 쓴다.
 * 예) t(item?.restday?.name) → localizedLabel(t, item?.restday)
 */
/**
 * localizedLabel과 같되 t 대신 i18next 전역 t를 쓴다(컴포넌트 밖 헬퍼·store 값 표시용).
 * 예) user.me.userRank → getLocalizedFieldOrI18n(user.me, 'userRank') (userRankEng 등 → t → 원본)
 */
export const getLocalizedFieldOrI18n = (obj, field = 'name', options) =>
	getLocalizedFieldOrTranslate(obj, field, undefined, (key) => i18n.t(key), options);

/**
 * 공휴일 표시명. 자동 생성 대체공휴일은 이름 끝에 '(대체휴일)'이 붙어 오므로 그 부분만 번역한다.
 * 겹친 휴일은 줄바꿈으로 합쳐져 와서 '(대체휴일)'이 여러 번 나올 수 있다.
 */
export const getHolidayDisplayName = (holiday) => {
	if (!holiday?.name) return '';
	let name = getLocalizedField(holiday) || '';
	// API 번역이 없는 시스템 휴일(휴일대체 등)은 번역 JSON에 키가 있을 때만 번역한다
	if (name === holiday.name && i18n.exists?.(name)) name = i18n.t(name);
	return String(name).split('(대체휴일)').join(i18n.t('(대체휴일)'));
};

export const localizedLabel = (t, obj, field = 'name', options) =>
	getLocalizedFieldOrTranslate(obj, field, undefined, t, options);

/**
 * 평면 문자열(row.depart 등)을 같은 이름의 원본 객체(users.department 등)로 번역한다.
 * 객체의 name이 값과 다르면 다른 대상일 수 있으므로 기존처럼 t(value)(t가 없으면 value)를 쓴다.
 * 예) localizedValueLabel(t, row.depart, row.users?.department)
 */
export const localizedValueLabel = (t, value, source, field = 'name', options) => {
	if (source && hasText(value) && source[field] === value) {
		return getLocalizedFieldOrTranslate(source, field, undefined, t, options);
	}
	return typeof t === 'function' && hasText(value) ? t(value) : value;
};

/**
 * 값이 원본 필드나 언어별 필드(어느 언어든) 중 하나와 같으면 true. 앞뒤 공백은 무시한다.
 * 엑셀 업로드처럼 사용자가 어느 언어로 적었는지 모르는 이름을 대상과 맞출 때 쓴다.
 * 예) matchesAnyLocalizedField(restday, 'Annual Leave') → restday.nameEng가 같으면 true
 */
export const matchesAnyLocalizedField = (obj, value, field = 'name', options = {}) => {
	if (!obj || !hasText(String(value ?? ''))) return false;
	const target = String(value).trim();
	const keys = [field, ...Object.keys(LANG_FIELD_SUFFIX).map((lang) => getLocalizedFieldKey(field, lang, options.localizedBase || field))];
	return keys.some((key) => hasText(obj[key]) && obj[key].trim() === target);
};

/** 엑셀 업로드 헤더를 맞춰 볼 언어(helpers/locales.jsx의 LANGUAGE_LIST) */
export const EXCEL_HEADER_LANGS = ['ko', 'en', 'vi', 'ch'];

/**
 * 업로드 모달이 열릴 때 부른다. 다른 언어의 번역도 불러와야 어느 언어 양식이든 헤더를 읽을 수 있다
 * @param {string[]} ns  헤더 번역이 들어 있는 네임스페이스
 */
export const loadExcelHeaderLangs = (ns) => i18n.reloadResources(EXCEL_HEADER_LANGS, ns);

/** 번역 키의 언어별 문구(불러온 언어만, 중복 제거). 번역이 없는 언어는 키를 그대로 돌려받으므로 키도 포함된다 */
export const getAllLangLabels = (key) =>
	[...new Set(EXCEL_HEADER_LANGS.map((lng) => String(i18n.getFixedT(lng)(key) ?? '').trim()))].filter(hasText);

/**
 * 엑셀 행(sheet_to_json 결과)의 헤더를 어느 언어로 적었든 원본(한국어) 키로 바꾼다. 모르는 헤더는 그대로 둔다
 * 업로드 데이터를 한국어 키로 다루는(서버 전송 포함) 화면에서 번역된 양식을 받을 때 쓴다
 * @param {Object[]} rows
 * @param {Array<string|string[]>} keys  한국어 원본 키(= 번역 키). 번역 키가 다르면 [원본 키, 번역 키]
 */
export const normalizeExcelHeaders = (rows = [], keys = []) => {
	const aliasMap = new Map();
	keys.forEach((entry) => {
		const [key, labelKey = key] = Array.isArray(entry) ? entry : [entry];
		aliasMap.set(key, key);
		getAllLangLabels(labelKey).forEach((label) => !aliasMap.has(label) && aliasMap.set(label, key));
	});
	return rows.map((row) =>
		Object.fromEntries(Object.entries(row).map(([header, value]) => [aliasMap.get(String(header).trim()) ?? header, value])),
	);
};

/** 컴포넌트용. 언어가 바뀌면 리렌더되도록 i18n.language를 구독한다 */
export const useLocalizedField = () => {
	const { i18n: instance } = useTranslation();
	return (obj, field = 'name', options) => getLocalizedField(obj, field, instance.language, options);
};

/** 컴포넌트용. API 번역값이 없으면 번역 JSON으로 대체한다 */
export const useLocalizedFieldOrTranslate = () => {
	const { t, i18n: instance } = useTranslation();
	return (obj, field = 'name', options) => getLocalizedFieldOrTranslate(obj, field, instance.language, t, options);
};
